import useSWRMutation from 'swr/mutation'
import { useRedirectIfSession } from '../hooks/useSessionProbe'
import { EyeOpenIcon, EyeClosedIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { Flex, Button, Container, TextField, IconButton, Text, Box, Callout, Theme, Heading } from '@radix-ui/themes'
import { Layout, CreateRoot } from '@/components/Layout.jsx'
import { fetcher } from '@/utils/fetcher.js'
import { useState, useEffect } from 'react'
import { z } from 'zod'
import startBanner from '@/assets/images/start/start-banner-desktop.png'
import startBannerTablet from '@/assets/images/start/start-banner-tablet-fix.png'
import startBannerMobile from '@/assets/images/start/start-banner-mobile.png'
import decorativeCurve from '@/assets/images/start/decorative-curve-element.svg'
import communityClientLogo from '@/assets/images/shared/community-client-logo.svg'
import { usePageTitle } from '../hooks/usePageTitle'

const goToForgotYourPassword = () => {
  window.location.href = '/en/forgot-password'
}

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email(),
  password: z.string().min(1, 'Password is required')
})

const emptyForm = { email: '', password: '' }

export default function Page () {
  usePageTitle({ title: 'Sign In' })
  const { probing } = useRedirectIfSession()
  const [formData, setFormData] = useState(emptyForm)
  const [formErrors, setFormErrors] = useState({})
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const { trigger: loginTrigger, data: loginData, error: loginError, isMutating: loginIsLoading, reset: loginReset } = useSWRMutation('/api/authentication/login', fetcher)

  useEffect(() => {
    if (loginError) {
      setFormErrors({ swr: loginError.responseMessage })
    } else if (loginData) {
      window.location.replace('/en/user')
    }
  }, [loginData, loginError])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const validateForm = () => {
    const result = loginSchema.safeParse(formData)
    setFormErrors(result.success ? {} : result.error.format())
    return result.success
  }

  useEffect(() => {
    isDirty && validateForm()
  }, [isDirty, formData])

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsDirty(true)
    if (!validateForm()) return
    loginReset()
    loginTrigger({
      method: 'POST',
      body: JSON.stringify(formData)
    })
  }

  const toggleIsPasswordVisible = () => {
    setIsPasswordVisible(!isPasswordVisible)
  }

  if (probing) {
    return <></>
  }

  return (
    <Layout>
      <Flex
        direction={{
          initial: 'column',
          lg: 'row'
        }}
        justify='between'
        height='100dvh'
        overflow={{
          initial: 'auto',
          lg: 'hidden'
        }}
      >
        {/* Background banner image full screen sm screens and then left column lg screens */}
        <Box flexGrow='1' inset='0' position={{ initial: 'absolute', lg: 'relative' }}>
          <picture>
            <source srcSet={startBanner} media='(min-width: 75em)' />
            <source srcSet={startBannerTablet} media='(min-width: 40em)' />
            <img className='start-banner' src={startBannerMobile} alt='Two people doing zenful yoga' />
          </picture>
        </Box>
        <Flex
          flexGrow='1'
          direction='column'
          justify={{ initial: 'between', lg: 'start' }}
          width='100%'
          maxWidth={{ initial: 'none', lg: '692px', xl: '40%' }}
          height={{ initial: 'initial', lg: '100%' }}
          overflow={{ initial: 'initial', lg: 'auto' }}
        >
          {/* Keep as spacer */}
          <Box py={{ initial: '4', sm: '6' }} position='relative' />
          <Flex
            py={{
              initial: '0',
              sm: '120px',
              lg: '24px',
              xl: '40px'
            }}
            position='relative'
            className='z-1'
            overflowX={{ initial: 'hidden', lg: 'visible' }}
            flexGrow={{ initial: '0', lg: '1' }}
            direction='column'
            justify={{ initial: 'end', lg: 'center' }}
          >
            <img
              className='decorative-curve'
              src={decorativeCurve}
              alt=''
              role='presentation'
            />
            <Box
              className='start-account-box'
              width='100%'
              maxWidth={{
                initial: '100%',
                sm: '380px'
              }}
              mx='auto'
              style={{ background: 'white' }}
            >
              <Container pt={{ initial: '4', md: '5' }} pb='5' px='4' position='relative' className='z-1' style={loginIsLoading ? { pointerEvents: 'none' } : null}>
                <Flex direction='column' align='center' gap='5'>
                  <Box>
                    <Box pb='3'>
                      <img
                        src={communityClientLogo}
                        alt='Community client'
                      />
                    </Box>
                    <Heading as='h1' mb='0' align='center' size='8' weight='medium'>Sign in</Heading>
                  </Box>
                  {/*
                  Field email
                  Field email
                  Field email
                */}
                  <Flex width='100%' direction='column' gap='2'>
                    <Text as='label' htmlFor='email' size='3' weight='bold' color='gray'>Email address</Text>
                    <TextField.Root
                      placeholder='Enter email address'
                      type='email'
                      id='email'
                      name='email'
                      value={formData.email}
                      onChange={handleChange}
                      variant={formErrors.email?._errors.length ? 'soft' : 'surface'}
                      color={formErrors.email?._errors.length ? 'red' : ''}
                      size='3'
                    />
                    {formErrors.email?._errors.length && <Text as='p' color='red'>{formErrors.email?._errors[0]}</Text>}
                  </Flex>
                  {/*
                    Field password
                    Field password
                    Field password
                    */}

                  <Flex width='100%' direction='column' gap='2'>
                    <Text as='label' htmlFor='password' size='3' weight='bold' color='gray'>Password</Text>
                    <TextField.Root
                      placeholder='Enter password'
                      type={isPasswordVisible ? 'text' : 'password'}
                      id='password'
                      name='password'
                      value={formData.password}
                      onChange={handleChange}
                      variant={formErrors.password?._errors.length ? 'soft' : 'surface'}
                      color={formErrors.password?._errors.length ? 'red' : ''}
                      size='3'
                    >
                      <TextField.Slot side='right'>
                        <Theme accentColor='gray' className='textField-slot-theme-wrapper'>
                          <IconButton onClick={toggleIsPasswordVisible} size='2' variant='ghost'>
                            {!isPasswordVisible && <EyeOpenIcon height='16' width='16' />}
                            {isPasswordVisible && <EyeClosedIcon height='16' width='16' />}
                          </IconButton>
                        </Theme>
                      </TextField.Slot>
                    </TextField.Root>
                    {formErrors.password?._errors.length && <Text as='p' color='red'>{formErrors.password?._errors[0]}</Text>}
                  </Flex>
                  <Flex width='100%' direction='column' gap='2'>
                    {!!formErrors?.swr && (
                      <Callout.Root color='red' role='alert'>
                        <Callout.Icon>
                          <ExclamationTriangleIcon />
                        </Callout.Icon>
                        <Callout.Text>
                          {formErrors.swr}
                        </Callout.Text>
                      </Callout.Root>
                    )}
                  </Flex>
                </Flex>
                <Box py='5'>
                  <Flex gap='2' direction='column'>
                    <Flex direction='column' py='2' px='4'>
                      <Button mx='0' size='4' variant='ghost' onClick={goToForgotYourPassword}>Forgot your password</Button>
                    </Flex>
                    <Button size='4' variant='solid' onClick={handleSubmit} loading={loginIsLoading}>Sign-in</Button>
                  </Flex>
                </Box>
              </Container>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </Layout>
  )
}

CreateRoot(Page)
